import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { IMPACT_LABELS, TopShopStatusBadge } from "@/components/topshop/topShopShared";
import { ADMIN_APPS_KEY, AdminAppRow, ListRow, NOT_ASSIGNED, communityLabel, orderedActiveCommunities, useTopShopLists } from "./useTopShopAdmin";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CELL_LABEL, useTopShopChallengeMatrix } from "./useTopShopChallengeAdmin";
import { RubricScorer } from "@/components/topshop/RubricScorer";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { evidenceCountLabel, useEvidenceCounts } from "@/components/topshop/TopShopChallengeEvidence";

const BUCKET = "top-shop-photos";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium break-words">{children || "-"}</dd>
    </div>
  );
}

export function TopShopApplicationSheet({ application, focusAreas, onClose }: {
  application: AdminAppRow | null; focusAreas: ListRow[]; onClose: () => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState<string | null>(null);
  const [fullPhoto, setFullPhoto] = useState<string | null>(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [pendingCommunity, setPendingCommunity] = useState("");
  const [confirmStatus, setConfirmStatus] = useState<"declined" | "waitlisted" | null>(null);
  const [emailApplicant, setEmailApplicant] = useState(true);
  const [moveOpen, setMoveOpen] = useState(false);
  const appId = application?.id;
  const { data: matrix } = useTopShopChallengeMatrix((application as any)?.program_id);
  const { data: lists } = useTopShopLists((application as any)?.program_id);
  const { data: evidenceCounts } = useEvidenceCounts(appId);
  const communityOptions = orderedActiveCommunities(lists?.communities ?? []);

  const activeAreas = focusAreas.filter((f) => f.is_active).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const { data: photos = [], isLoading: photosLoading } = useQuery({
    queryKey: ["admin", "top-shop", "photos", appId],
    enabled: !!appId,
    queryFn: async () => {
      const { data, error } = await supabase.from("program_application_photos").select("*")
        .eq("application_id", appId!).order("sort_order").order("created_at");
      if (error) throw error;
      const paths = (data ?? []).map((p) => p.storage_path);
      if (!paths.length) return [];
      const { data: signed } = await supabase.storage.from(BUCKET).createSignedUrls(paths, 3600);
      return (data ?? []).map((p, i) => ({ ...p, url: signed?.[i]?.signedUrl ?? null }));
    },
  });

  useEffect(() => { setNotes(application?.admin_notes ?? ""); }, [appId, application?.admin_notes]);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ADMIN_APPS_KEY });
    qc.invalidateQueries({ queryKey: ["admin", "top-shop", "challenge-matrix"] });
    qc.invalidateQueries({ queryKey: ["admin", "top-shop-applicant-ids"] });
  };

  const setStatus = async (status: string, sendEmail = false) => {
    if (!appId) return;
    setSaving(status);
    const { error } = await supabase.from("program_applications")
      .update({ status, reviewed_by: user?.id ?? null, reviewed_at: new Date().toISOString() }).eq("id", appId);
    setSaving(null);
    if (error) { toast.error(error.message); return; }
    if (sendEmail) {
      const { data, error: fnErr } = await supabase.functions.invoke("notify-topshop-status", { body: { application_id: appId } });
      if (fnErr || (data as any)?.error) toast.warning("Status updated. The email could not be sent.");
      else if ((data as any)?.reason === "already_sent") toast.success("Status updated. This applicant was already emailed about this status.");
      else if ((data as any)?.sent) toast.success("Status updated and email sent");
      else toast.warning("Status updated. The email could not be sent.");
    } else {
      toast.success("Status updated");
    }
    invalidate();
  };

  const moveToCommunity = async () => {
    if (!appId) return;
    setSaving("move");
    const { error } = await supabase.rpc("admin_move_applicant_to_community", { p_application_id: appId });
    setSaving(null);
    setMoveOpen(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Moved to the EAB Connect community");
    invalidate();
  };

  const saveNotes = async () => {
    if (!appId) return;
    setSaving("notes");
    const { error } = await supabase.from("program_applications").update({ admin_notes: notes || null }).eq("id", appId);
    setSaving(null);
    if (error) { toast.error(error.message); return; }
    toast.success("Notes saved");
    invalidate();
  };

  const saveCommunity = async (communityId: string | null) => {
    if (!appId) return false;
    setSaving("community");
    const { error } = await supabase.from("program_applications").update({ community_id: communityId }).eq("id", appId);
    setSaving(null);
    if (error) { toast.error(error.message); return false; }
    toast.success("Community updated");
    invalidate();
    return true;
  };

  const focusNames = (application?.focus_area_ids ?? []).map((id) => focusAreas.find((f) => f.id === id)?.name ?? id);

  return (
    <>
      <Sheet open={!!application} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          {application && (
            <div className="space-y-6">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 flex-wrap">
                  {application.business_name} <TopShopStatusBadge status={application.status} />
                </SheetTitle>
              </SheetHeader>

              <dl className="grid grid-cols-2 gap-3">
                <Field label="Name">{application.full_name}</Field>
                <Field label="Email">{application.email}</Field>
                <Field label="Phone">{application.phone}</Field>
                <Field label="WhatsApp number">{application.whatsapp_number}</Field>
                <Field label="Parish">{application.parish}</Field>
                <Field label="Community">{application.community_name}</Field>
                <Field label="Type of shop">{application.type_name}</Field>
                <Field label="Hurricane impact">{IMPACT_LABELS[application.hurricane_impact_level] ?? application.hurricane_impact_level}</Field>
                <Field label="Applied">
                  {format(new Date(application.created_at), "MMM d, yyyy")}
                  {application.applicant_updated_at && (
                    <span className="block text-xs font-normal text-muted-foreground">
                      Last updated by applicant: {format(new Date(application.applicant_updated_at), "MMM d, yyyy h:mm a")}
                    </span>
                  )}
                </Field>
                {application.reviewed_at && <Field label="Reviewed">{format(new Date(application.reviewed_at), "MMM d, yyyy")}</Field>}
                <div className="col-span-2"><Field label="Focus areas">{focusNames.join(", ")}</Field></div>
                <div className="col-span-2">
                  <dt className="text-xs text-muted-foreground">Why they want to join</dt>
                  <dd className="text-sm font-medium break-words whitespace-pre-line">{application.why_join ?? "Not provided (applied before this question was added)"}</dd>
                </div>
                <div className="col-span-2"><Field label="Hurricane description">{application.hurricane_impact_description}</Field></div>
              </dl>

              <section className="space-y-2">
                <h3 className="font-semibold">Photos</h3>
                {photosLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : photos.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No photos yet.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((p: any) => (
                      <button key={p.id} type="button" className="aspect-square overflow-hidden rounded-md border bg-muted"
                        onClick={() => p.url && setFullPhoto(p.url)} aria-label="View photo full size">
                        {p.url && <img src={p.url} alt="Shop photo" className="h-full w-full object-cover" />}
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <section className="space-y-2">
                <h3 className="font-semibold">Status</h3>
                <div className="flex flex-wrap gap-2">
                  {[["accepted", "Accept"], ["waitlisted", "Waitlist"], ["declined", "Decline"], ["withdrawn", "Mark withdrawn"], ["completed", "Mark completed"], ["submitted", "Reset to Submitted"]].map(([v, l]) => (
                    <Button key={v} size="sm" variant={application.status === v ? "default" : "outline"}
                      disabled={!!saving || application.status === v}
                      onClick={() => {
                        if (v === "accepted" && !application.community_id) { setPendingCommunity(""); setAcceptOpen(true); }
                        else if (v === "declined" || v === "waitlisted") { setEmailApplicant(true); setConfirmStatus(v); }
                        else setStatus(v);
                      }}>
                      {saving === v && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{l}
                    </Button>
                  ))}
                </div>
                {(application.status === "withdrawn" || application.status === "completed") && !application.moved_to_community_at && (
                  <Button size="sm" variant="secondary" disabled={!!saving} onClick={() => setMoveOpen(true)}>
                    Move to EAB Connect community
                  </Button>
                )}
                {application.moved_to_community_at && (
                  <p className="text-xs text-muted-foreground">
                    Moved to EAB Connect community on {format(new Date(application.moved_to_community_at), "MMM d, yyyy")}
                  </p>
                )}
                {application.status_email_sent_for && application.status_email_sent_at && (
                  <p className="text-xs text-muted-foreground">
                    Emailed: {application.status_email_sent_for === "declined" ? "Declined" : "Waitlisted"} on {format(new Date(application.status_email_sent_at), "MMM d, yyyy")}
                  </p>
                )}
              </section>

              <section className="space-y-2">
                <Label htmlFor="ts-community" className="font-semibold">Community</Label>
                <Select value={application.community_id ?? NOT_ASSIGNED} disabled={!!saving}
                  onValueChange={(v) => saveCommunity(v === NOT_ASSIGNED ? null : v)}>
                  <SelectTrigger id="ts-community"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NOT_ASSIGNED}>Not assigned</SelectItem>
                    {communityOptions.map((c) => <SelectItem key={c.id} value={c.id}>{communityLabel(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </section>

              <section className="space-y-2">
                <Label htmlFor="ts-notes" className="font-semibold">Admin notes</Label>
                <Textarea id="ts-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
                <Button size="sm" onClick={saveNotes} disabled={!!saving}>
                  {saving === "notes" && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save
                </Button>
              </section>

              <section className="space-y-2">
                <h3 className="font-semibold">Challenges</h3>
                <ul className="divide-y rounded-md border text-sm">
                  {(matrix?.columns ?? []).map((c) => {
                    const cell = matrix?.cells[application.id]?.[c.focusAreaId];
                    return (
                      <li key={c.focusAreaId} className="flex items-center justify-between gap-2 p-2">
                        <span>{c.number} {c.title}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-muted-foreground">{CELL_LABEL[cell?.status ?? "none"]}</span>
                          {cell?.submissionId && <span className="text-xs text-muted-foreground">{evidenceCountLabel(evidenceCounts?.[c.focusAreaId])}</span>}
                          {cell?.submissionId && <Link className="text-primary hover:underline" to={`/instructor/submissions/${cell.submissionId}`}>Open</Link>}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>

              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Scores</h3>
                  <span className="text-sm">Total: <strong>{Number.isInteger(application.total_score) ? application.total_score : application.total_score.toFixed(1)}</strong> of {matrix?.activeMax ?? 0}</span>
                </div>
                <RubricScorer applicationId={application.id} />
              </section>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign a community first?</AlertDialogTitle>
            <AlertDialogDescription>
              Shops without a community appear as Unassigned on the leaderboard. Choose where this shop operates, or My community is not listed if it is outside the program communities.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Select value={pendingCommunity} onValueChange={setPendingCommunity}>
            <SelectTrigger aria-label="Community"><SelectValue placeholder="Select community" /></SelectTrigger>
            <SelectContent>
              {communityOptions.map((c) => <SelectItem key={c.id} value={c.id}>{communityLabel(c)}</SelectItem>)}
            </SelectContent>
          </Select>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="outline" disabled={!!saving} onClick={async () => { setAcceptOpen(false); await setStatus("accepted"); }}>
              Accept without community
            </Button>
            <Button disabled={!pendingCommunity || !!saving} onClick={async () => {
              const ok = await saveCommunity(pendingCommunity);
              if (!ok) return;
              setAcceptOpen(false);
              await setStatus("accepted");
            }}>Assign and accept</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmStatus} onOpenChange={(o) => !o && setConfirmStatus(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmStatus === "declined" ? "Decline this application?" : "Move this application to the waitlist?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {application?.full_name || "This applicant"} becomes a regular EAB Connect member. If they were in the Top Shop Challenge 2026 cohort, they leave it. Their courses, progress, certificates and badges are kept, and their courses become self-paced.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2">
            <Checkbox id="ts-email-applicant" checked={emailApplicant} onCheckedChange={(c) => setEmailApplicant(c === true)} />
            <Label htmlFor="ts-email-applicant">Email the applicant</Label>
          </div>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button disabled={!!saving} onClick={async () => {
              const s = confirmStatus!; setConfirmStatus(null); await setStatus(s, emailApplicant);
            }}>{confirmStatus === "declined" ? "Decline" : "Waitlist"}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={moveOpen} onOpenChange={setMoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move to EAB Connect community?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes {application?.full_name || "this applicant"} from the Top Shop Challenge 2026 cohort. Their courses, progress, certificates and badges are kept, and their courses become self-paced. Their application stays on record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button disabled={!!saving} onClick={moveToCommunity}>
              {saving === "move" && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Move
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!fullPhoto} onOpenChange={(o) => !o && setFullPhoto(null)}>
        <DialogContent className="max-w-4xl">
          <DialogTitle className="sr-only">Shop photo</DialogTitle>
          {fullPhoto && <img src={fullPhoto} alt="Shop photo full size" className="w-full h-auto max-h-[80vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </>
  );
}
